import { useEffect, useState, useCallback } from "react";
import { Icon, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtDateTime, fmtRelative } from "../../lib/dates.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import useInterval from "../../hooks/useInterval.js";
import {
  getSystemHealth,
  listWebhooks,
  retryWebhook,
  itemsOf,
  getPlatformSettings,
  setMaintenanceMode,
  setInactivityConfig,
  setUsageReportConfig,
  runInactivityNow,
  runUsageReportNow,
} from "../../lib/adminApi";

const DOW = [
  ["0", "Sunday"], ["1", "Monday"], ["2", "Tuesday"], ["3", "Wednesday"],
  ["4", "Thursday"], ["5", "Friday"], ["6", "Saturday"],
];

const PAGE_SIZE = 50;

const bentoCard = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 1px 0 rgba(255,255,255,.02), 0 4px 16px rgba(0,0,0,.12)",
};

const statusBadge = (s) =>
  s === "processed" ? "badge-success" :
  s === "received"  ? "badge-warning" :
  s === "failed"    ? "badge-error"   :
  s === "duplicate" ? "badge-muted"   : "badge-muted";

function SystemInner() {
  const { toast, openModal, closeModal } = useApp();
  const [health, setHealth] = useState(null);
  const [webhooksResp, setWebhooksResp] = useState({ items: [], total: 0, hasMore: false, page: 1 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [maint, setMaint] = useState({ enabled: false, message: "" });
  const [maintDraft, setMaintDraft] = useState("");
  const [savingMaint, setSavingMaint] = useState(false);
  const [inactivity, setInactivity] = useState({ enabled: false, days: 3 });
  const [usage, setUsage] = useState({ enabled: false, dayOfWeek: 1 });
  const [busyJob, setBusyJob] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));
    try {
      const [h, w, s] = await Promise.all([
        getSystemHealth(),
        listWebhooks(`?${params}`),
        getPlatformSettings().catch(() => null),
      ]);
      setHealth(h);
      setWebhooksResp(itemsOf(w));
      if (s?.maintenance) {
        setMaint(s.maintenance);
        setMaintDraft(s.maintenance.message || "");
      }
      if (s?.inactivity) setInactivity(s.inactivity);
      if (s?.usageReport) setUsage(s.usageReport);
    } catch (e) {
      toast(e.message || "Failed to load system status", "error");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, toast]);

  const saveMaintenance = useCallback(async (enabled, message) => {
    setSavingMaint(true);
    try {
      const res = await setMaintenanceMode(enabled, message);
      setMaint(res.maintenance);
      setMaintDraft(res.maintenance.message || "");
      toast(
        res.maintenance.enabled ? "Maintenance mode is now ON" : "Maintenance mode is now OFF",
        res.maintenance.enabled ? "warning" : "success",
      );
    } catch (e) {
      toast(e.message || "Failed to update maintenance mode", "error");
    } finally {
      setSavingMaint(false);
    }
  }, [toast]);

  const saveInactivity = useCallback(async (next) => {
    try {
      const res = await setInactivityConfig(next.enabled, next.days);
      setInactivity(res.inactivity);
      toast("Inactivity reminder settings saved", "success");
    } catch (e) { toast(e.message || "Failed to save", "error"); }
  }, [toast]);

  const saveUsage = useCallback(async (next) => {
    try {
      const res = await setUsageReportConfig(next.enabled, next.dayOfWeek);
      setUsage(res.usageReport);
      toast("Usage report settings saved", "success");
    } catch (e) { toast(e.message || "Failed to save", "error"); }
  }, [toast]);

  const runJob = useCallback(async (which) => {
    setBusyJob(which);
    try {
      const res = which === "inactivity" ? await runInactivityNow() : await runUsageReportNow();
      const n = res.sent ?? 0;
      toast(`Sent ${n} email${n === 1 ? "" : "s"}`, "success");
    } catch (e) {
      toast(e.message || "Failed to run job", "error");
    } finally {
      setBusyJob("");
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useInterval(load, 30_000);
  useEffect(() => { setPage(1); }, [statusFilter]);

  const HealthTile = ({ label, ok, icon, optional }) => (
    <div style={bentoCard}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, color: "var(--text-muted)", fontWeight: 600 }}>
            {label}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6, color: ok ? "var(--success)" : optional ? "var(--text-muted)" : "var(--error)" }}>
            {ok ? "Operational" : optional ? "Not configured" : "Down"}
          </div>
        </div>
        <div
          style={{
            width: 40, height: 40, borderRadius: 12,
            display: "grid", placeItems: "center",
            background: ok ? "rgba(16,185,129,.15)" : optional ? "rgba(148,163,184,.15)" : "rgba(239,68,68,.15)",
            color: ok ? "#10B981" : optional ? "#94A3B8" : "#EF4444",
            position: "relative",
          }}
        >
          <Icon name={icon} size={18} />
        </div>
      </div>
    </div>
  );

  const viewWebhook = (w) => {
    openModal(
      <>
        <div className="modal-head">
          <h3>{w.eventType}</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
            <Row label="Status" value={w.status} />
            <Row label="Provider" value={w.provider} />
            <Row label="Received" value={fmtDateTime(w.receivedAt)} />
            <Row label="Processed" value={w.processedAt ? fmtDateTime(w.processedAt) : "—"} />
            <Row label="Attempts" value={w.attempts ?? 1} />
            <Row label="External ID" value={w.externalEventId || "—"} />
            {w.error && <Row label="Error" value={w.error} />}
          </div>
          <h4 style={{ fontSize: 13, marginBottom: 6 }}>Payload</h4>
          <pre style={{
            background: "var(--surface-2, rgba(148,163,184,0.08))",
            padding: 12,
            borderRadius: 8,
            fontSize: 11,
            lineHeight: 1.5,
            overflow: "auto",
            maxHeight: 320,
          }}>
            {JSON.stringify(w.payload, null, 2)}
          </pre>
        </div>
        <div className="modal-foot" style={{ gap: 8 }}>
          {w.status !== "processed" && (
            <button className="btn btn-primary" onClick={async () => {
              try {
                await retryWebhook(w.id);
                toast("Webhook re-processed", "success");
                closeModal();
                load();
              } catch (e) { toast(e.message, "error"); }
            }}>
              <Icon name="refresh-cw" size={14} /> Retry
            </button>
          )}
          <button className="btn btn-ghost" onClick={closeModal}>Close</button>
        </div>
      </>,
      "lg",
    );
  };

  const totalPages = Math.max(1, Math.ceil(webhooksResp.total / PAGE_SIZE));

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">System Health</h1>
          <p className="page-sub">Backend services + webhook ingestion</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}>
          <Icon name="refresh-cw" size={14} /> Refresh
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <HealthTile label="Database" ok={!!health?.database} icon="database" />
        <HealthTile label="AI Service" ok={!!health?.ai} icon="cpu" />
        <HealthTile label="Razorpay" ok={!!health?.razorpay} optional={!health?.razorpay} icon="credit-card" />
        <HealthTile label="SMTP" ok={!!health?.smtp} optional={!health?.smtp} icon="mail" />
      </div>

      {/* ── Maintenance Mode ─────────────────────────────────── */}
      <div
        className="card"
        style={{
          marginBottom: 18,
          border: maint.enabled ? "1px solid rgba(245,158,11,.5)" : "1px solid var(--border)",
          background: maint.enabled ? "rgba(245,158,11,.06)" : "var(--surface)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                display: "grid", placeItems: "center",
                background: maint.enabled ? "rgba(245,158,11,.15)" : "rgba(148,163,184,.12)",
                color: maint.enabled ? "#F59E0B" : "var(--text-muted)",
              }}
            >
              <Icon name="construction" size={20} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                Maintenance Mode
                <span className={`badge ${maint.enabled ? "badge-warning" : "badge-muted"}`}>
                  <span className="badge-dot" /> {maint.enabled ? "Active" : "Off"}
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 3, maxWidth: 520 }}>
                When active, all tenants (owners, managers, employees) see a maintenance screen and the
                data API returns 503. Platform admins keep full access.
              </div>
            </div>
          </div>
          <button
            className={`btn ${maint.enabled ? "btn-ghost" : "btn-primary"}`}
            disabled={savingMaint}
            onClick={() => saveMaintenance(!maint.enabled, maintDraft)}
          >
            <Icon name={maint.enabled ? "play" : "power"} size={14} />
            {savingMaint ? "Saving…" : maint.enabled ? "Turn off" : "Enable maintenance"}
          </button>
        </div>

        <div className="field" style={{ marginTop: 14, marginBottom: 0 }}>
          <label>Message shown to users (optional)</label>
          <textarea
            className="input"
            rows={2}
            maxLength={500}
            placeholder="We're performing scheduled maintenance and will be back shortly."
            value={maintDraft}
            onChange={(e) => setMaintDraft(e.target.value)}
            style={{ resize: "vertical" }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button
              className="btn btn-ghost btn-sm"
              disabled={savingMaint || maintDraft === (maint.message || "")}
              onClick={() => saveMaintenance(maint.enabled, maintDraft)}
            >
              <Icon name="save" size={13} /> Save message
            </button>
          </div>
        </div>
      </div>

      {/* ── Engagement jobs: inactivity + usage report ──────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14, marginBottom: 18 }}>
        {/* Inactivity reminder */}
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, display: "grid", placeItems: "center", background: "rgba(59,130,246,.14)", color: "#3B82F6" }}>
                <Icon name="bell-ring" size={18} />
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700 }}>Inactivity Reminder</div>
            </div>
            <label className="checkbox" style={{ margin: 0 }}>
              <input type="checkbox" checked={inactivity.enabled}
                onChange={(e) => saveInactivity({ ...inactivity, enabled: e.target.checked })} />
            </label>
          </div>
          <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: "10px 0 14px", lineHeight: 1.5 }}>
            Email employees who haven't filed a report in the last N days. Runs daily at 10:00 IST.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <label style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Inactive for</label>
            <select className="input" style={{ width: 90 }} value={inactivity.days}
              onChange={(e) => saveInactivity({ ...inactivity, days: Number(e.target.value) })}>
              {[1, 2, 3, 4, 5, 7, 10, 14].map((d) => <option key={d} value={d}>{d} day{d === 1 ? "" : "s"}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }}
              disabled={busyJob === "inactivity"} onClick={() => runJob("inactivity")}>
              <Icon name="send" size={13} /> {busyJob === "inactivity" ? "Sending…" : "Run now"}
            </button>
          </div>
        </div>

        {/* Usage report */}
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, display: "grid", placeItems: "center", background: "rgba(16,185,129,.14)", color: "#10B981" }}>
                <Icon name="bar-chart-2" size={18} />
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700 }}>Usage Report by Email</div>
            </div>
            <label className="checkbox" style={{ margin: 0 }}>
              <input type="checkbox" checked={usage.enabled}
                onChange={(e) => saveUsage({ ...usage, enabled: e.target.checked })} />
            </label>
          </div>
          <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: "10px 0 14px", lineHeight: 1.5 }}>
            Weekly digest emailed to every company owner (reports, active staff, tasks, participation).
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <label style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Send every</label>
            <select className="input" style={{ width: 130 }} value={usage.dayOfWeek}
              onChange={(e) => saveUsage({ ...usage, dayOfWeek: Number(e.target.value) })}>
              {DOW.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }}
              disabled={busyJob === "usage"} onClick={() => runJob("usage")}>
              <Icon name="send" size={13} /> {busyJob === "usage" ? "Sending…" : "Run now"}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Razorpay Webhooks</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {webhooksResp.total} events received
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[["", "All"], ["processed", "Processed"], ["received", "Received"], ["failed", "Failed"]].map(([k, l]) => (
              <button
                key={k}
                className={`filter-chip ${statusFilter === k ? "active" : ""}`}
                onClick={() => setStatusFilter(k)}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <TableSkeleton columns={5} rows={5} />
        ) : webhooksResp.items.length === 0 ? (
          <EmptyState title="No webhook events" desc="No webhooks have been received yet." icon="webhook" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Received</th>
                  <th>Processed</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {webhooksResp.items.map((w) => (
                  <tr key={w.id} style={{ cursor: "pointer" }} onClick={() => viewWebhook(w)}>
                    <td data-label="Event" style={{ fontWeight: 600, fontSize: 13 }}>{w.eventType}</td>
                    <td data-label="Status">
                      <span className={`badge ${statusBadge(w.status)}`}>
                        <span className="badge-dot" /> {w.status}
                      </span>
                    </td>
                    <td data-label="Attempts">{w.attempts ?? 1}</td>
                    <td data-label="Received" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {fmtRelative(w.receivedAt)}
                    </td>
                    <td data-label="Processed" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {w.processedAt ? fmtRelative(w.processedAt) : "—"}
                    </td>
                    <td data-label="">
                      <button className="icon-btn" title="View" onClick={(e) => { e.stopPropagation(); viewWebhook(w); }}>
                        <Icon name="eye" size={14} />
                      </button>
                      {w.status !== "processed" && (
                        <button
                          className="icon-btn"
                          title="Retry"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try { await retryWebhook(w.id); toast("Webhook re-processed", "success"); load(); }
                            catch (err) { toast(err.message, "error"); }
                          }}
                        >
                          <Icon name="refresh-cw" size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && webhooksResp.total > 0 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 16, alignItems: "center" }}>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <Icon name="chevron-left" size={14} /> Prev
            </button>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Page {page} of {totalPages}
            </span>
            <button className="btn btn-ghost btn-sm" disabled={!webhooksResp.hasMore} onClick={() => setPage((p) => p + 1)}>
              Next <Icon name="chevron-right" size={14} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 12 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", minWidth: 110 }}>{label}</span>
      <span style={{ fontSize: 13, wordBreak: "break-all" }}>{value}</span>
    </div>
  );
}

export default function AdminSystem() {
  return (
    <AdminPageBoundary>
      <SystemInner />
    </AdminPageBoundary>
  );
}
